import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, UserGroupIcon, PencilIcon, TrashIcon, XMarkIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from '../components/ConfirmDialog';

const AdminLeadershipPage = () => {
    const [activeTab, setActiveTab] = useState('management_team');
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, memberId: null });

    // Helper to get tab label
    const getTabLabel = (tab) => tab === 'management_team' ? 'Management Team' : 'Board of Directors';

    // Fetch members
    const fetchMembers = async () => {
        try {
            setLoading(true);
            // We use the admin endpoint which returns all members
            const response = await axios.get('/api/leadership');
            setMembers(response.data.data || []);
        } catch (error) {
            console.error('Error fetching members:', error);
            toast.error('Failed to load members');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers();
    }, []);

    const filteredMembers = members.filter(m => m.type === activeTab);

    const handleAddNew = () => {
        setEditingMember(null);
        setIsModalOpen(true);
    };

    const handleEdit = (member) => {
        setEditingMember(member);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, memberId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/leadership/${deleteConfirm.memberId}`);
            toast.success('Member deleted successfully');
            fetchMembers();
        } catch (error) {
            console.error('Error deleting member:', error);
            toast.error('Failed to delete member');
        } finally {
            setDeleteConfirm({ isOpen: false, memberId: null });
        }
    };

    const toggleStatus = async (member) => {
        try {
            // Need to send FormData even for simple update if strict about endpoints, 
            // but controller handles req.body too? The controller checks req.file.
            // Let's use FormData to be safe as the controller expects multipart often or check implementation.
            // Controller uses req.body fields.
            const formData = new FormData();
            formData.append('name', member.name);
            formData.append('designation', member.designation);
            formData.append('type', member.type);
            formData.append('order_index', member.order_index);
            formData.append('bio', typeof member.bio === 'string' ? member.bio : JSON.stringify(member.bio));
            formData.append('is_active', !member.is_active);

            await axios.put(`/api/leadership/${member.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(`Member ${member.is_active ? 'deactivated' : 'activated'} successfully`);
            fetchMembers();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Leadership Team</h1>
                    <p className="text-slate-500 mt-1">Manage executive team and board of directors</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-1.5 inline-flex gap-1">
                <button
                    onClick={() => setActiveTab('management_team')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'management_team'
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <UserGroupIcon className="h-5 w-5" />
                    Management Team
                </button>
                <button
                    onClick={() => setActiveTab('board_of_directors')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'board_of_directors'
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <UserGroupIcon className="h-5 w-5" />
                    Board of Directors
                </button>
            </div>

            {/* List Section */}
            <div className="space-y-6">
                <div className="flex justify-end">
                    <button
                        onClick={handleAddNew}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add New Member
                    </button>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-2xl h-64 animate-pulse border border-slate-100"></div>
                        ))}
                    </div>
                ) : filteredMembers.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
                        <UserGroupIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-800">No members found</h3>
                        <p className="text-slate-500 mb-6">Start by adding your first member to the {getTabLabel(activeTab)}</p>
                        <button
                            onClick={handleAddNew}
                            className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold"
                        >
                            Add New Member
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredMembers.map((member) => (
                            <div key={member.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                                     <div className="aspect-square w-full relative overflow-hidden bg-slate-100">
                                    {member.image_url ? (
                                        <img
                                            src={member.image_url.startsWith('http') ? member.image_url : `${member.image_url}`}
                                            alt={member.name}
                                             className="w-full h-full object-cover object-top"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                                            <UserGroupIcon className="w-16 h-16" />
                                        </div>
                                    )}
                                    <div className="absolute top-4 right-4 flex gap-2">
                                        <button
                                            onClick={() => toggleStatus(member)}
                                            className={`p-2 rounded-full glass-effect transition-colors ${member.is_active ? 'bg-green-500/90 text-white' : 'bg-slate-500/90 text-white'}`}
                                            title={member.is_active ? "Active" : "Inactive"}
                                        >
                                            {member.is_active ? <CheckCircleIcon className="w-5 h-5" /> : <XCircleIcon className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h3 className="text-lg font-bold text-slate-800">{member.name}</h3>
                                    <p className="text-blue-600 text-sm font-medium mb-4">{member.designation}</p>

                                    <div className="flex justify-between items-center pt-4 border-t border-slate-50 mt-4">
                                        <span className="text-xs text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded">
                                            Order: {member.order_index}
                                        </span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleEdit(member)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            >
                                                <PencilIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(member.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <TrashIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <LeadershipModal
                    member={editingMember}
                    activeTab={activeTab}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => {
                        setIsModalOpen(false);
                        fetchMembers();
                    }}
                />
            )}

            {/* Delete Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, memberId: null })}
                onConfirm={confirmDelete}
                title="Delete Member"
                message="Are you sure you want to delete this member? This action cannot be undone."
            />
        </div>
    );
};

// Sub-component for form modal
const LeadershipModal = ({ member, activeTab, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        designation: '',
        bio: '',
        type: activeTab,
        order_index: 0,
        is_active: true
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (member) {
            // Bio comes as array of strings from backend usually, need to join for textarea
            let bioText = '';
            // Backend sends 'bio' as JSON array or stringified JSON array
            // Assuming controller sends it parsed as array if using `res.json(data)` and DB driver handles parsing or we parsed it.
            // Wait, my controller stringifies it on save, but `getMembers` returns raw DB rows usually?
            // `mysql2` default configuration does NOT auto-parse JSON columns unless configured.
            // My controller `getAllMembers` just does `SELECT *`. So `bio` will be a string (JSON string) or object if driver parses.
            // Let's handle both.
            let bioArray = [];
            try {
                if (typeof member.bio === 'string') {
                    bioArray = JSON.parse(member.bio);
                } else if (Array.isArray(member.bio)) {
                    bioArray = member.bio;
                }
            } catch (e) {
                console.error("Error parsing bio:", e);
            }
            bioText = Array.isArray(bioArray) ? bioArray.join('\n\n') : '';

            setFormData({
                name: member.name,
                designation: member.designation,
                bio: bioText,
                type: member.type,
                order_index: member.order_index,
                is_active: member.is_active === 1 || member.is_active === true
            });
             setImagePreview(member.image_url ? (member.image_url.startsWith('http') ? member.image_url : `${member.image_url.startsWith('/') || member.image_url.startsWith('\\') ? '' : '/'}${member.image_url.replace(/\\/g, '/')}`) : null);
        } else {
            // Defaults for new
            setFormData({
                name: '',
                designation: '',
                bio: '',
                type: activeTab,
                order_index: 0,
                is_active: true
            });
        }
    }, [member, activeTab]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('designation', formData.designation);
            data.append('type', formData.type);
            data.append('order_index', formData.order_index);
            data.append('is_active', formData.is_active);

            // Split bio by double newline
            const bioArray = formData.bio.split(/\n\s*\n/).filter(p => p.trim() !== '');
            data.append('bio', JSON.stringify(bioArray));

            if (imageFile) {
                data.append('image', imageFile);
            }

            if (member) {
                await axios.put(`/api/leadership/${member.id}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Member updated successfully');
            } else {
                await axios.post('/api/leadership', data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Member created successfully');
            }
            onSave();
        } catch (error) {
            console.error('Error saving member:', error);
            toast.error('Failed to save member');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <h2 className="text-xl font-bold text-slate-800">
                        {member ? 'Edit Member' : 'Add New Member'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-8 overflow-y-auto">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Image Upload */}
                        {/* Image Upload */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Member Photo <span className="text-slate-400">(Optional)</span>
                            </label>

                            <div className="flex gap-6 items-start">
                                {/* Upload Area */}
                                <div className="flex-1">
                                    <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 transition-colors bg-slate-50 hover:bg-slate-100 group">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="hidden"
                                        />
                                        <div className="bg-white p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 shadow-sm group-hover:scale-110 transition-transform">
                                            <UserGroupIcon className="h-6 w-6 text-blue-600" />
                                        </div>
                                        <p className="text-sm font-medium text-slate-700 mb-1">Click to upload photo</p>
                                        <p className="text-xs text-slate-400">SVG, PNG, JPG or GIF (max 5MB)</p>
                                    </label>
                                </div>

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className="flex-shrink-0">
                                        <div className="w-32 h-40 rounded-xl overflow-hidden border-2 border-slate-200 shadow-sm bg-slate-100">
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    placeholder="John Doe"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Designation</label>
                                <input
                                    type="text"
                                    name="designation"
                                    value={formData.designation}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    placeholder="Director"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Bio</label>
                            <p className="text-xs text-slate-500 mb-2">Separate paragraphs with double newlines.</p>
                            <textarea
                                name="bio"
                                value={formData.bio}
                                onChange={handleChange}
                                rows={6}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"
                                placeholder={"Mr. John Doe is...\n\nHe has over 20 years of experience..."}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Order Index</label>
                                <input
                                    type="number"
                                    name="order_index"
                                    value={formData.order_index}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                />
                            </div>

                            <div className="flex items-center gap-3 pt-8">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    <span className="ml-3 text-sm font-medium text-slate-700">Active</span>
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-50 transition-all border border-transparent hover:border-slate-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Member'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminLeadershipPage;
